import { useCallback } from "react"

import { useRecoilState } from "recoil"

import { UIState } from "../atoms/pluginStudioAtom"
import { uiStateSelector } from "../selectors"

type PanelId = keyof UIState["panels"]

export function useUIState() {
  const [uiState, setUIState] = useRecoilState(uiStateSelector)

  // Set panel visibility
  const setPanelVisibility = useCallback(
    (panelId: PanelId, visible: boolean) => {
      setUIState((currentState) => ({
        ...currentState,
        panels: {
          ...currentState.panels,
          [panelId]: {
            ...currentState.panels[panelId],
            visible,
          },
        },
      }))
    },
    [],
  )

  // Toggle panel visibility
  const togglePanelVisibility = useCallback((panelId: PanelId) => {
    setUIState((currentState) => ({
      ...currentState,
      panels: {
        ...currentState.panels,
        [panelId]: {
          ...currentState.panels[panelId],
          visible: !currentState.panels[panelId].visible,
        },
      },
    }))
  }, [])

  // Set panel size (width or height)
  const setPanelSize = useCallback(
    (
      panelId: PanelId,
      size: string,
      dimension: "width" | "height" = "width",
    ) => {
      setUIState((currentState) => ({
        ...currentState,
        panels: {
          ...currentState.panels,
          [panelId]: {
            ...currentState.panels[panelId],
            [dimension]: size,
          },
        },
      }))
    },
    [],
  )

  // Reset all panel settings to default
  const resetPanelSettings = useCallback(() => {
    setUIState({
      panels: {
        fileExplorer: {
          visible: true,
          width: "250px",
        },
        testPanel: {
          visible: true,
          height: "44.257%",
        },
        liveDataPanel: {
          visible: true,
          height: "44.257%",
        },
        previewPanel: {
          visible: true,
          width: "35%",
        },
      },
    })
  }, [])

  return {
    uiState,
    setPanelVisibility,
    togglePanelVisibility,
    setPanelSize,
    resetPanelSettings,
  }
}
